import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Camera, MapPin, Search, RefreshCw, X, CheckCircle, AlertTriangle,
  Calendar, CircleDashed, Building2,
} from "lucide-react";
import { useAuth } from "./App";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

/**
 * National Inspectors Upload Queue — dedicated view for Aly.
 *
 * Mirror of Vitória's Inspectors Upload page but restricted to National
 * orders. Auto-refreshes every 30s. Mobile-friendly.
 *
 * Business rule: even if an order has no appointment / pointing data, if
 * the inspector uploaded photos the row MUST appear (flagged "Missing
 * Pointing" so Aly can resolve it).
 */
const NationalInspectorsUploadPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  // Aly (National Transmission) opens orders in her dedicated read-only
  // viewer (/aly-order/:id) which has the photo gallery + zip download.
  // Admins keep the generic /order/:id route for full edit access.
  const openOrder = useCallback((id) => {
    if (user?.role === "National Transmission") {
      navigate(`/aly-order/${id}`);
    } else {
      navigate(`/order/${id}`);
    }
  }, [navigate, user]);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/national-inspectors-upload-queue`, {
        params: showHistory ? { show_all: true } : {},
      });
      setItems(res.data || []);
    } catch (e) {
      console.error("Failed to fetch National uploads queue:", e);
    } finally { setLoading(false); }
  }, [showHistory]);

  useEffect(() => {
    fetchQueue();
    const iv = setInterval(fetchQueue, 30000);
    return () => clearInterval(iv);
  }, [fetchQueue]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(o =>
      (o.order_number || "").toLowerCase().includes(q) ||
      (o.address || "").toLowerCase().includes(q) ||
      (o.city || "").toLowerCase().includes(q) ||
      (o.state || "").toLowerCase().includes(q) ||
      (o.assigned_inspector || "").toLowerCase().includes(q) ||
      (o.last_inspector || "").toLowerCase().includes(q) ||
      (o.cliente || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  // Dashboard counters
  const counts = useMemo(() => {
    const c = { total: items.length, ready: 0, missingAppt: 0, missingPointing: 0, pendingReview: 0, uploadedToday: 0 };
    for (const o of items) {
      if (o.upload_status === "Ready") c.ready++;
      if (o.upload_status === "Pending Review") c.pendingReview++;
      if (o.appointment_status === "No Appointment") c.missingAppt++;
      if (o.appointment_status === "Missing Pointing") c.missingPointing++;
      if (o.uploaded_today) c.uploadedToday++;
    }
    return c;
  }, [items]);

  const formatTs = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return iso; }
  };

  // Visual color for row left-border / appt pill (green/yellow/red/blue matrix)
  const rowTint = (o) => {
    if (o.uploaded_today) return "border-l-4 border-l-[#2F5D8A]"; // Blue today
    if (o.appointment_status === "Missing Pointing") return "border-l-4 border-l-[#B04840]"; // Red missing data
    if (o.appointment_status === "No Appointment") return "border-l-4 border-l-[#B08A3E]"; // Yellow
    if (o.upload_status === "Ready") return "border-l-4 border-l-[#6B8F62]"; // Green ready
    return "border-l-4 border-l-transparent";
  };

  const apptPill = (status) => {
    const map = {
      "With Appointment": "bg-[#6B8F62]/15 text-[#4A6B42] border border-[#6B8F62]/30",
      "No Appointment":   "bg-[#B08A3E]/15 text-[#6F4E1D] border border-[#B08A3E]/30",
      "Missing Pointing": "bg-[#B04840]/15 text-[#7A2E2A] border border-[#B04840]/40",
      "Pending Review":   "bg-[#2F5D8A]/10 text-[#2F5D8A] border border-[#2F5D8A]/30",
    };
    return map[status] || "bg-gray-100 text-gray-600 border border-gray-200";
  };

  const uploadPill = (status) => {
    const map = {
      "Ready":          "bg-[#6B8F62] text-white",
      "Pending Review": "bg-[#2F5D8A] text-white",
      "Transmitted":    "bg-gray-400 text-white",
    };
    return map[status] || "bg-gray-200 text-gray-700";
  };

  return (
    <div className="space-y-4" data-testid="national-inspectors-upload-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Building2 className="h-6 w-6 text-[#2F5D8A]" /> National Inspectors Upload
          </h1>
          <p className="text-sm text-gray-500">National orders where inspectors uploaded photos — {showHistory ? "full history (read-only)" : "today's uploads only. Resets daily at midnight."}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHistory(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
              showHistory
                ? "bg-[#2F5D8A] text-white border-[#2F5D8A]"
                : "bg-white text-gray-700 border-gray-200 hover:border-[#2F5D8A]"
            }`}
            data-testid="national-history-toggle"
          >
            {showHistory ? "Back to Today" : "Show History"}
          </button>
          <button
            onClick={fetchQueue}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 hover:border-[#2F5D8A] text-gray-700 text-xs font-semibold rounded-lg"
            data-testid="national-refresh-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Stat label="Total" value={`${filtered.length} / ${counts.total}`} tone="blue" testId="national-count-total" />
        <Stat label="Ready" value={counts.ready} tone="green" testId="national-count-ready" />
        <Stat label="No Appointment" value={counts.missingAppt} tone="amber" testId="national-count-no-appt" />
        <Stat label="Missing Pointing" value={counts.missingPointing} tone="red" testId="national-count-missing-pointing" />
        <Stat label="Uploaded Today" value={counts.uploadedToday} tone="blue-soft" testId="national-count-today" />
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by order #, inspector, address, city, state..."
            className="w-full pl-8 pr-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm focus:outline-none focus:border-[#D1A054]"
            data-testid="national-search-input"
          />
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading && items.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading queue...</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <Camera className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm font-semibold text-gray-500">No pending National uploads</p>
            <p className="text-xs text-gray-400 mt-1">Rows appear here automatically when an inspector uploads photos to a National order.</p>
          </div>
        ) : (
          <>
            {/* Desktop / tablet table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-200 text-[10px] text-gray-500 uppercase tracking-wider">
                    <th className="text-left px-3 py-2 font-semibold">Order #</th>
                    <th className="text-left px-3 py-2 font-semibold">Address</th>
                    <th className="text-left px-3 py-2 font-semibold">City / State</th>
                    <th className="text-left px-3 py-2 font-semibold">Inspector</th>
                    <th className="text-left px-3 py-2 font-semibold">Upload</th>
                    <th className="text-center px-3 py-2 font-semibold">Photos</th>
                    <th className="text-left px-3 py-2 font-semibold">Appointment</th>
                    <th className="text-left px-3 py-2 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(o => (
                    <tr
                      key={o.id}
                      onClick={() => openOrder(o.id)}
                      className={`hover:bg-[#F0F4F9] cursor-pointer ${rowTint(o)}`}
                      data-testid={`national-upload-row-${o.order_number}`}
                    >
                      <td className="px-3 py-2.5 text-xs font-bold text-[#2F5D8A] whitespace-nowrap">{o.order_number}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-700 min-w-[160px] max-w-[260px] truncate" title={o.address}>{o.address || "-"}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">{[o.city, o.state].filter(Boolean).join(", ") || "-"}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-700 whitespace-nowrap">{o.last_inspector || o.assigned_inspector || "-"}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">{formatTs(o.upload_last_at)}</td>
                      <td className="px-3 py-2.5 text-xs text-center">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#DCEAE4] text-[#4A7A56] font-bold">
                          <Camera className="h-3 w-3" />{o.photo_count || 0}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold ${apptPill(o.appointment_status)}`}>
                          {o.appointment_status === "With Appointment" && <CheckCircle className="h-3 w-3" />}
                          {o.appointment_status === "No Appointment" && <Calendar className="h-3 w-3" />}
                          {o.appointment_status === "Missing Pointing" && <AlertTriangle className="h-3 w-3" />}
                          {o.appointment_status}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold ${uploadPill(o.upload_status)}`}>
                          {o.upload_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {filtered.map(o => (
                <div
                  key={o.id}
                  onClick={() => openOrder(o.id)}
                  className={`p-3 active:bg-[#F0F4F9] cursor-pointer ${rowTint(o)}`}
                  data-testid={`national-upload-card-${o.order_number}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <p className="text-sm font-bold text-[#2F5D8A]">#{o.order_number}</p>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#DCEAE4] text-[#4A7A56] font-bold text-[10px]">
                      <Camera className="h-3 w-3" />{o.photo_count || 0}
                    </span>
                  </div>
                  <p className="text-xs text-gray-700 truncate">{o.address || "-"}</p>
                  <p className="text-[11px] text-gray-500 truncate">{[o.city, o.state].filter(Boolean).join(", ") || "-"}</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">👷 {o.last_inspector || o.assigned_inspector || "Unknown"}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">⏱ {formatTs(o.upload_last_at)}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold ${apptPill(o.appointment_status)}`}>
                      {o.appointment_status}
                    </span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold ${uploadPill(o.upload_status)}`}>
                      {o.upload_status}
                    </span>
                    {o.uploaded_today && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#2F5D8A] text-white text-[10px] font-bold">TODAY</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="text-[11px] text-gray-400 px-1">
        <MapPin className="inline h-3 w-3 mr-1" />
        National-only view. Auto-refreshes every 30s. <span className="font-semibold">Live queue shows today's uploads only</span> — yesterday's items auto-clear at midnight (history preserved, use Show History to browse). <CircleDashed className="inline h-3 w-3" /> Tap a row to open the order and review photos.
      </div>
    </div>
  );
};

/* ---------------------------- Small stat card ---------------------------- */

const Stat = ({ label, value, tone, testId }) => {
  const tones = {
    blue:      "bg-[#2F5D8A]/8 border-[#2F5D8A]/25 text-[#2F5D8A]",
    green:     "bg-[#6B8F62]/10 border-[#6B8F62]/30 text-[#4A6B42]",
    amber:     "bg-[#B08A3E]/10 border-[#B08A3E]/30 text-[#6F4E1D]",
    red:       "bg-[#B04840]/10 border-[#B04840]/30 text-[#7A2E2A]",
    "blue-soft": "bg-[#DCE4ED] border-[#2F5D8A]/20 text-[#2F5D8A]",
  };
  return (
    <div className={`rounded-lg border p-3 ${tones[tone] || tones.blue}`} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</p>
      <p className="text-xl font-bold mt-0.5">{value}</p>
    </div>
  );
};

export default NationalInspectorsUploadPage;
